import {
  Button,
  Card,
  CardHeader,
  Modal,
  SegmentedControl,
  StatusPill,
  StatusText,
  Switch,
  Textarea,
  formatCivilDate,
  formatCurrency,
  useSnackbar,
  type StatusTone,
} from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck2, History, ListChecks, Scale, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  ErrorCard,
  ListFilterBar,
  LoadingCard,
  RequestAlert,
  SummaryStrip,
} from '../../app/data-view.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import { PageHeader } from '../../app/page-title.js';
import { getApiError } from '../../lib/api.js';
import {
  closingApi,
  type AccountPart,
  type DuplicateQuestion,
  type MonthClosingView,
  type MonthStatus,
  type PeriodAdjustment,
  type UnitYear,
} from './closing-api.js';

/// O fechamento contábil (ADR 0064): fechar transforma o DRE do mês de uma unidade em número
/// oficial. A régua dos meses (uma unidade) ou a tabela de unidades (várias) mostram a situação;
/// o painel do mês confere, pergunta, fecha, reabre e ajusta.

export type ClosingDestination =
  | 'cash-closing'
  | 'classification'
  | 'reconciliation'
  | 'dre'
  | 'commissions'
  | 'payables'
  | 'receivables';

const SHORT_MONTHS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];
const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const monthIndex = (month: string) => Number(month.slice(5, 7)) - 1;
const monthName = (month: string) => `${MONTH_NAMES[monthIndex(month)]} de ${month.slice(0, 4)}`;
const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const money = (cents: number) => `${cents < 0 ? '−' : ''}${formatCurrency(Math.abs(cents))}`;
const nextMonthOf = (month: string) => {
  const [year, number] = month.split('-').map(Number) as [number, number];
  return number === 12 ? `${year + 1}-01` : `${year}-${String(number + 1).padStart(2, '0')}`;
};
const previousMonthOf = (month: string) => {
  const [year, number] = month.split('-').map(Number) as [number, number];
  return number === 1 ? `${year - 1}-12` : `${year}-${String(number - 1).padStart(2, '0')}`;
};
const dateTime = (instant: string) =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(new Date(instant))
    .replace(', ', ' às ');

const STATUS: Record<MonthStatus, { label: string; short: string; tone: StatusTone; dot: string }> =
  {
    CLOSED: { label: 'Fechado', short: 'fechado', tone: 'positive', dot: 'bg-positive' },
    READY: { label: 'Pronto para fechar', short: 'pronto', tone: 'brand', dot: 'bg-brand' },
    BLOCKED: { label: 'Com bloqueio', short: 'bloqueado', tone: 'danger', dot: 'bg-danger' },
    WAITING: {
      label: 'Espera o mês anterior',
      short: 'espera',
      tone: 'neutral',
      dot: 'bg-content/25',
    },
    RUNNING: { label: 'Em andamento', short: 'em curso', tone: 'progress', dot: 'bg-info' },
    FUTURE: { label: 'Ainda não começou', short: '', tone: 'neutral', dot: 'bg-content/10' },
  };

/// O que a unidade tem para fazer agora: o primeiro mês ainda aberto, se já acabou.
function nextOf(unit: UnitYear): { month: string; status: MonthStatus } | null {
  if (unit.closedUntil) {
    const month = nextMonthOf(unit.closedUntil);
    return unit.months.find((one) => one.month === month) ?? null;
  }
  return null;
}

/// O mês que a tela abre: o próximo a fechar; sem nada fechado, o último que acabou.
function initialMonth(unit: UnitYear | undefined, today: string): string {
  const todayMonth = today.slice(0, 7);
  const next = unit ? nextOf(unit) : null;
  if (next && next.month < todayMonth) return next.month;
  if (next) return next.month;
  return previousMonthOf(todayMonth);
}

export interface ClosingPermissions {
  close: boolean;
  reopen: boolean;
  adjust: boolean;
  /// Abrir Comissões e Despesas e Receitas a partir da conferência. Sem a permissão, só o valor aparece.
  commissions?: boolean;
  obligations?: boolean;
}

export function AccountingClosingView({
  today,
  activeUnitId,
  unitCount,
  can,
  onOpen,
  onOpenUnit,
  switching,
}: {
  today: string;
  /// A unidade da sessão. Sem ela, a tela é a visão somada, só de leitura.
  activeUnitId: string | null;
  /// Com uma unidade só, a tela não fala em unidade (ADR 0039).
  unitCount: number;
  can: ClosingPermissions;
  onOpen: (destination: ClosingDestination) => void;
  /// Trocar a unidade da sessão: abrir uma unidade a partir da tabela, ou voltar à visão somada.
  onOpenUnit: (unitId: string | null) => void;
  switching: boolean;
}) {
  const period = useOperationalPeriod(today);
  const year = Number((period.month === 'ALL' ? today : period.month).slice(0, 4));
  const overview = useQuery({
    queryKey: ['accounting', 'closing', 'overview', year, today, activeUnitId],
    queryFn: () => closingApi.overview(year, today),
  });
  const single = unitCount <= 1;
  const consolidated = activeUnitId === null && !single;
  const unit =
    overview.data?.units.find((one) => one.id === activeUnitId) ?? overview.data?.units[0];
  const [chosen, setChosen] = useState<string | null>(null);
  // Na visão somada, o último mês que acabou: é ele que diz quem ainda falta fechar.
  const month =
    chosen ?? (consolidated ? previousMonthOf(today.slice(0, 7)) : initialMonth(unit, today));

  return (
    <section aria-labelledby="accounting-closing-title" className="min-w-0">
      <PageHeader
        trail="Contabilidade gerencial / Fechamento"
        id="accounting-closing-title"
        title="Fechamento contábil"
        description="Fechar o mês transforma o DRE daquele mês em número oficial: ele fica guardado e não muda sozinho. Antes de fechar, o caixa do mês precisa estar fechado e os valores que podem estar contados duas vezes precisam de resposta."
        actions={
          !single && !consolidated ? (
            <Button
              variant="outline"
              size="sm"
              disabled={switching}
              onClick={() => onOpenUnit(null)}
            >
              Ver todas as unidades
            </Button>
          ) : null
        }
      />
      {overview.isError ? (
        <ErrorCard message={getApiError(overview.error)} />
      ) : !overview.data ? (
        <LoadingCard label="Lendo a situação dos meses" />
      ) : consolidated ? (
        <NetworkView
          units={overview.data.units}
          year={year}
          today={today}
          month={month}
          onMonth={setChosen}
          onOpenUnit={onOpenUnit}
          switching={switching}
        />
      ) : unit ? (
        <>
          <MonthStrip unit={unit} year={year} single={single} month={month} onMonth={setChosen} />
          <MonthPanel
            month={month}
            today={today}
            single={single}
            unitName={unit.name}
            can={can}
            onOpen={onOpen}
            onMonth={setChosen}
          />
        </>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// Uma unidade: a régua dos doze meses

function MonthStrip({
  unit,
  year,
  single,
  month,
  onMonth,
}: {
  unit: UnitYear;
  year: number;
  single: boolean;
  month: string;
  onMonth: (month: string) => void;
}) {
  const next = nextOf(unit);
  return (
    <Card className="mb-4">
      <CardHeader
        icon={<CalendarCheck2 className="size-5" aria-hidden="true" />}
        title={`${year}${single ? '' : ` · ${unit.name}`} · ${
          unit.closedUntil ? `fechado até ${monthName(unit.closedUntil)}` : 'nenhum mês fechado'
        }`}
      />
      <p className="mb-3 text-sm text-content-muted">
        {!unit.closedUntil
          ? 'O primeiro mês a fechar é escolha sua; depois dele, os meses fecham em ordem.'
          : next && (next.status === 'READY' || next.status === 'BLOCKED')
            ? `Próximo a fechar: ${monthName(next.month)} (${STATUS[next.status].label.toLowerCase()}).`
            : 'Em dia: todos os meses que já acabaram estão fechados.'}
      </p>
      <div
        role="group"
        aria-label={`Meses de ${year}`}
        className="grid grid-cols-6 gap-1.5 sm:grid-cols-12"
      >
        {unit.months.map((one) => {
          const status = STATUS[one.status];
          const disabled = one.status === 'FUTURE';
          return (
            <button
              key={one.month}
              type="button"
              disabled={disabled}
              aria-pressed={one.month === month}
              aria-label={`${capital(monthName(one.month))}: ${status.label}${one.changed ? ', mudou depois de fechado' : ''}`}
              onClick={() => onMonth(one.month)}
              className={`flex min-w-0 cursor-pointer flex-col items-center gap-1 rounded-lg border px-1 py-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-40 ${
                one.month === month
                  ? 'border-content bg-surface-soft'
                  : 'border-surface-border bg-surface-card hover:bg-content/5'
              }`}
            >
              <b className="text-[13px]">{SHORT_MONTHS[monthIndex(one.month)]}</b>
              <span
                className={`size-2.5 rounded-full ${one.changed ? 'bg-warning' : status.dot}`}
                aria-hidden="true"
              />
              <span className="truncate text-[11px] text-content-faint">
                {one.changed ? 'mudou' : status.short}
              </span>
            </button>
          );
        })}
      </div>
      <Legend />
    </Card>
  );
}

function Legend() {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-muted">
      {(['CLOSED', 'READY', 'BLOCKED', 'WAITING', 'RUNNING'] as const).map((status) => (
        <li key={status} className="inline-flex items-center gap-1.5">
          <span className={`size-2.5 rounded-full ${STATUS[status].dot}`} aria-hidden="true" />
          {STATUS[status].label}
        </li>
      ))}
      <li className="inline-flex items-center gap-1.5">
        <span className="size-2.5 rounded-full bg-warning" aria-hidden="true" />
        Mudou depois de fechado
      </li>
    </ul>
  );
}

// ---------------------------------------------------------------------------------------------
// Várias unidades: a tabela, só de leitura

const FILTERS = [
  { value: 'all', label: 'Todas' },
  { value: 'BLOCKED', label: 'Com bloqueio' },
  { value: 'READY', label: 'Prontas para fechar' },
  { value: 'DONE', label: 'Em dia' },
] as const;
type NetworkFilter = (typeof FILTERS)[number]['value'];

const plain = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

function NetworkView({
  units,
  year,
  today,
  month,
  onMonth,
  onOpenUnit,
  switching,
}: {
  units: UnitYear[];
  year: number;
  today: string;
  month: string;
  onMonth: (month: string) => void;
  onOpenUnit: (unitId: string) => void;
  switching: boolean;
}) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<NetworkFilter>('all');
  const todayMonth = today.slice(0, 7);
  const lastEnded = previousMonthOf(todayMonth);
  const stateOf = (unit: UnitYear): Exclude<NetworkFilter, 'all'> => {
    const next = nextOf(unit);
    if (!next) return unit.closedUntil ? 'DONE' : 'BLOCKED';
    if (next.month >= todayMonth) return 'DONE';
    return next.status === 'READY' ? 'READY' : 'BLOCKED';
  };
  const counts = { BLOCKED: 0, READY: 0, DONE: 0 };
  for (const unit of units) counts[stateOf(unit)] += 1;
  const visible = units.filter(
    (unit) =>
      (filter === 'all' || stateOf(unit) === filter) && plain(unit.name).includes(plain(search)),
  );
  const closedAll = (index: number) =>
    units.every((unit) => unit.months[index]?.status === 'CLOSED');
  const officialUntil = (() => {
    let last: string | null = null;
    for (let index = 0; index < 12; index += 1) {
      if (!closedAll(index)) break;
      last = units[0]!.months[index]!.month;
    }
    return last;
  })();
  const closedLast = units.filter(
    (unit) => unit.months[monthIndex(lastEnded)]?.status === 'CLOSED',
  ).length;
  const missing = units.filter((unit) => unit.months[monthIndex(month)]?.status !== 'CLOSED');

  return (
    <>
      <Card className="mb-4">
        <CardHeader
          icon={<CalendarCheck2 className="size-5" aria-hidden="true" />}
          title={`Situação de ${year} por unidade`}
        />
        <p className="mb-3 text-sm text-content-muted">
          {closedLast} de {units.length} unidades fecharam {monthName(lastEnded)}. Para fechar ou
          reabrir, abra a unidade: cada unidade fecha o próprio mês.
        </p>
        <ListFilterBar
          showViewPicker={false}
          variant="panel"
          searchLabel="Buscar unidade"
          search={search}
          onSearchChange={setSearch}
          count={`${visible.length} ${visible.length === 1 ? 'unidade' : 'unidades'}`}
        >
          <SegmentedControl
            label="Mostrar"
            compact
            value={filter}
            onChange={setFilter}
            options={FILTERS.map((one) => ({
              value: one.value,
              label: one.value === 'all' ? one.label : `${one.label} (${counts[one.value]})`,
            }))}
          />
        </ListFilterBar>
        <div className="mt-3 max-h-[28rem] overflow-auto rounded-xl border border-surface-border">
          <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
            <caption className="sr-only">Situação de cada mês por unidade</caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 top-0 z-20 border-b border-surface-border bg-surface-card px-3 py-2 text-left text-xs font-semibold text-content/70"
                >
                  Unidade
                </th>
                {SHORT_MONTHS.map((label) => (
                  <th
                    key={label}
                    scope="col"
                    className="sticky top-0 z-10 border-b border-surface-border bg-surface-card px-1 py-2 text-center text-xs font-semibold text-content/70"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-surface-soft font-semibold">
                <th
                  scope="row"
                  className="sticky left-0 z-10 border-b border-surface-border bg-surface-soft px-3 py-2 text-left"
                >
                  Unidades somadas
                  <span className="block text-xs font-normal text-content-muted">
                    {officialUntil
                      ? `oficial até ${monthName(officialUntil)}`
                      : 'nenhum mês oficial'}
                  </span>
                </th>
                {SHORT_MONTHS.map((label, index) => {
                  const one = units[0]?.months[index];
                  if (!one) return <td key={label} className="border-b border-surface-border" />;
                  const closed = units.filter(
                    (unit) => unit.months[index]?.status === 'CLOSED',
                  ).length;
                  const future = one.month >= todayMonth;
                  return (
                    <td key={label} className="border-b border-surface-border p-0 text-center">
                      <button
                        type="button"
                        disabled={one.month > todayMonth}
                        aria-pressed={one.month === month}
                        aria-label={`${capital(monthName(one.month))}: ${closed} de ${units.length} unidades fecharam`}
                        onClick={() => onMonth(one.month)}
                        className={`flex w-full cursor-pointer flex-col items-center gap-0.5 px-1 py-2 disabled:cursor-not-allowed disabled:opacity-40 ${one.month === month ? 'ring-2 ring-inset ring-content' : ''}`}
                      >
                        <span
                          className={`size-2.5 rounded-full ${closed === units.length ? 'bg-positive' : future ? 'bg-content/10' : 'bg-content/25'}`}
                          aria-hidden="true"
                        />
                        {!future && closed < units.length ? (
                          <span className="text-[11px] font-medium text-content-muted">
                            {closed}/{units.length}
                          </span>
                        ) : null}
                      </button>
                    </td>
                  );
                })}
              </tr>
              {visible.map((unit) => (
                <tr key={unit.id}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 border-b border-surface-border/60 bg-surface-card p-0 text-left font-normal"
                  >
                    <button
                      type="button"
                      disabled={switching}
                      onClick={() => onOpenUnit(unit.id)}
                      className="w-full cursor-pointer px-3 py-2 text-left hover:bg-content/5 disabled:cursor-wait"
                    >
                      <span className="block font-medium">{unit.name}</span>
                      <span className="block text-xs text-content-muted">
                        {unit.closedUntil
                          ? `fechado até ${monthName(unit.closedUntil)}`
                          : 'nenhum mês fechado'}
                      </span>
                    </button>
                  </th>
                  {unit.months.map((one) => (
                    <td key={one.month} className="border-b border-surface-border/60 text-center">
                      <span
                        role="img"
                        aria-label={`${capital(monthName(one.month))}: ${STATUS[one.status].label}${one.changed ? ', mudou depois de fechado' : ''}`}
                        className={`inline-block size-2.5 rounded-full ${one.changed ? 'bg-warning' : STATUS[one.status].dot}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-3 py-4 text-sm text-content-muted">
                    Nenhuma unidade encontrada com esse filtro.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <Legend />
      </Card>
      <Card>
        <CardHeader
          icon={<Scale className="size-5" aria-hidden="true" />}
          title={`${capital(monthName(month))} · unidades somadas`}
          action={
            <StatusPill tone={missing.length === 0 ? 'positive' : 'warning'}>
              {missing.length === 0 ? 'Oficial' : 'Prévia'}
            </StatusPill>
          }
        />
        {month >= todayMonth ? (
          <p className="text-sm text-content-muted">O mês ainda não acabou.</p>
        ) : missing.length === 0 ? (
          <p className="text-sm text-content-muted">
            Todas as unidades fecharam: o DRE somado deste mês é oficial.
          </p>
        ) : (
          <>
            <p className="mb-3 text-sm text-content-muted">
              {units.length - missing.length} de {units.length} unidades fecharam. O DRE somado
              deste mês continua prévia até as outras {missing.length} fecharem.
            </p>
            <ul className="divide-y divide-surface-border rounded-xl border border-surface-border">
              {missing.map((one) => {
                const status = one.months[monthIndex(month)]!.status;
                return (
                  <li
                    key={one.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                  >
                    <span>
                      <span className="block font-medium">{one.name}</span>
                      <StatusText tone={STATUS[status].tone}>{STATUS[status].label}</StatusText>
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={switching}
                      onClick={() => onOpenUnit(one.id)}
                    >
                      Abrir {one.name}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// O mês da unidade

function MonthPanel({
  month,
  today,
  single,
  unitName,
  can,
  onOpen,
  onMonth,
}: {
  month: string;
  today: string;
  single: boolean;
  unitName: string;
  can: ClosingPermissions;
  onOpen: (destination: ClosingDestination) => void;
  onMonth: (month: string) => void;
}) {
  const view = useQuery({
    queryKey: ['accounting', 'closing', 'month', month, today],
    queryFn: () => closingApi.month(month, today),
  });
  if (view.isError) return <ErrorCard message={getApiError(view.error)} />;
  if (!view.data) return <LoadingCard label={`Conferindo ${monthName(month)}`} />;
  const data = view.data;
  const where = single ? '' : ` na unidade ${unitName}`;
  const status = STATUS[data.status];
  const title =
    data.status === 'READY' || data.status === 'BLOCKED'
      ? `Fechar ${monthName(month)}`
      : capital(monthName(month));
  return (
    <Card>
      <CardHeader
        icon={<ListChecks className="size-5" aria-hidden="true" />}
        title={`${title}${single ? '' : ` · ${unitName}`}`}
        action={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
      />
      {data.status === 'RUNNING' || data.status === 'FUTURE' ? (
        <p className="mb-4 text-sm text-content-muted">
          O mês ainda não acabou. Ele pode ser fechado a partir de 1º de{' '}
          {monthName(nextMonthOf(month))}.
        </p>
      ) : data.status === 'WAITING' ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-content-muted">
            Os meses fecham em ordem. Feche antes {monthName(data.previousOpen!)}.
          </p>
          <Button size="sm" variant="outline" onClick={() => onMonth(data.previousOpen!)}>
            Ir para {monthName(data.previousOpen!)}
          </Button>
        </div>
      ) : data.status === 'CLOSED' ? (
        <ClosedMonth data={data} month={month} today={today} where={where} can={can} />
      ) : (
        <Conference
          data={data}
          month={month}
          today={today}
          where={where}
          can={can}
          onOpen={onOpen}
        />
      )}
      {data.adjustmentsInto.length > 0 ? (
        <Section title={`Ajustes de meses anteriores lançados em ${monthName(month)}`}>
          <AdjustmentList adjustments={data.adjustmentsInto} showOrigin />
        </Section>
      ) : null}
    </Card>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[.1em] text-content-faint">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Check({
  ok,
  warn,
  title,
  children,
  action,
}: {
  ok: boolean;
  warn?: boolean;
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const tone: StatusTone = ok ? 'positive' : warn ? 'warning' : 'danger';
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-surface-border p-3 sm:flex-row sm:items-start">
      <StatusPill tone={tone} className="shrink-0 self-start">
        {ok ? 'Ok' : warn ? 'Conferir' : 'Impede'}
      </StatusPill>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-medium">{title}</p>
        <div className="text-sm text-content-muted">{children}</div>
      </div>
      {action ? <div className="flex shrink-0 flex-wrap gap-2">{action}</div> : null}
    </div>
  );
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['accounting'] });
}

function Conference({
  data,
  month,
  today,
  where,
  can,
  onOpen,
}: {
  data: MonthClosingView;
  month: string;
  today: string;
  where: string;
  can: ClosingPermissions;
  onOpen: (destination: ClosingDestination) => void;
}) {
  const snackbar = useSnackbar();
  const refresh = useRefresh();
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const pending = data.duplicates.filter((one) => !one.review);
  const ack = data.acknowledgement;
  const needsAck =
    ack.noAccountCents > 0 || ack.waitingCents > 0 || ack.pendingCommissionsCents > 0;
  const open = data.openItems;
  const blocked = data.status === 'BLOCKED';
  const close = useMutation({
    mutationFn: () => closingApi.close({ month, today, acknowledged: ack }),
    onSuccess: async () => {
      setConfirming(false);
      snackbar.success(`${capital(monthName(month))} fechado${where}.`);
      await refresh();
    },
  });
  const canClose = !blocked && (!needsAck || acknowledged);
  return (
    <>
      <p className="mb-4 text-sm text-content-muted">
        Confira o mês. Ao fechar, o DRE de {monthName(month)}
        {where ? ` desta unidade` : ''} fica guardado como está agora.
      </p>
      <Section title="O que impede fechar">
        <div className="space-y-2">
          <Check
            ok={data.cashClosed}
            title={`Caixa de ${monthName(month)} ${data.cashClosed ? 'fechado' : 'aberto'}`}
            action={
              data.cashClosed ? null : (
                <Button size="sm" variant="outline" onClick={() => onOpen('cash-closing')}>
                  Abrir o fechamento do caixa
                </Button>
              )
            }
          >
            {data.cashClosed
              ? 'O extrato de todas as contas financeiras foi tratado e o caixa do mês foi fechado.'
              : `O fechamento do caixa de ${monthName(month)} ainda não foi feito${where}. Ele fica em Financeiro › Caixa e extrato, na aba Fechamento.`}
          </Check>
          <Check
            ok={pending.length === 0}
            title={
              pending.length === 0
                ? 'Nenhum valor contado duas vezes sem resposta'
                : `${pending.length} ${pending.length === 1 ? 'valor pode estar contado' : 'valores podem estar contados'} duas vezes`
            }
          >
            {data.duplicates.length === 0
              ? 'Nenhum dia do mês tem receita lançada no extrato e, ao mesmo tempo, receita das vendas ainda não conciliada.'
              : 'No mesmo dia há dinheiro lançado no extrato como receita e o mesmo tipo de recebimento registrado nas vendas. Se for o mesmo dinheiro, o resultado está maior do que deveria. Só uma pessoa sabe dizer.'}
          </Check>
          {data.duplicates.map((question) => (
            <DuplicateCard key={question.day} question={question} canAnswer={can.close} />
          ))}
        </div>
      </Section>
      <Section title="Para conferir">
        {needsAck ? (
          <div className="space-y-2">
            {ack.noAccountCents > 0 ? (
              <Check
                ok={false}
                warn
                title="Sem conta do plano"
                action={
                  <Button size="sm" variant="outline" onClick={() => onOpen('classification')}>
                    Classificar
                  </Button>
                }
              >
                {formatCurrency(ack.noAccountCents)} deste mês entram no resultado na linha "Sem
                conta do plano": o total está certo, mas não se sabe em que linha do DRE eles ficam.
              </Check>
            ) : null}
            {ack.waitingCents > 0 ? (
              <Check
                ok={false}
                warn
                title="Aguardando conciliação"
                action={
                  <Button size="sm" variant="outline" onClick={() => onOpen('reconciliation')}>
                    Abrir a conciliação
                  </Button>
                }
              >
                {formatCurrency(ack.waitingCents)} do extrato deste mês ainda não foram ligados a
                uma despesa ou receita e ficam fora do DRE.
              </Check>
            ) : null}
            {ack.pendingCommissionsCents > 0 ? (
              <Check
                ok={false}
                warn
                title="Comissões sem apurar"
                action={
                  can.commissions ? (
                    <Button size="sm" variant="outline" onClick={() => onOpen('commissions')}>
                      Abrir Comissões
                    </Button>
                  ) : undefined
                }
              >
                {formatCurrency(ack.pendingCommissionsCents)} de comissão
                {open?.commissions?.professionals
                  ? ` de ${open.commissions.professionals === 1 ? '1 profissional' : `${open.commissions.professionals} profissionais`}`
                  : ''}{' '}
                nasceram até o fim de {monthName(month)} e ainda não foram apuradas. Não estão no
                resultado deste mês. Se fechar assim, entram na apuração de um mês seguinte, e o
                custo cai naquele mês.
              </Check>
            ) : null}
            <div className="rounded-xl border border-warning/40 bg-warning/10 px-4 py-3">
              <Switch checked={acknowledged} onChange={setAcknowledged} disabled={!can.close}>
                Sei que esses valores existem e quero fechar {monthName(month)} assim mesmo. A
                confirmação fica registrada no fechamento.
              </Switch>
            </div>
          </div>
        ) : (
          <p className="text-sm text-content-muted">
            Nada a conferir: tudo tem conta do plano, nada aguarda conciliação e não há comissão sem
            apurar.
          </p>
        )}
      </Section>
      {open ? <OpenItems open={open} can={can} onOpen={onOpen} /> : null}
      <Section title="Resultado que será guardado">
        <MonthSummary data={data} />
        <ClosingParts parts={data.closingParts} />
      </Section>
      {can.close ? (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          {blocked ? (
            <span className="text-sm text-content-muted">Resolva o que impede fechar.</span>
          ) : !canClose ? (
            <span className="text-sm text-content-muted">Confirme a conferência para fechar.</span>
          ) : null}
          <Button disabled={!canClose} onClick={() => setConfirming(true)}>
            Fechar {monthName(month)}
          </Button>
        </div>
      ) : (
        <p className="mt-4 text-sm text-content-muted">
          Seu perfil pode ver, mas não fechar o mês. Peça a quem tem a permissão "Fechar o mês
          contábil".
        </p>
      )}
      <Modal
        open={confirming}
        onOpenChange={(open) => {
          if (!close.isPending) setConfirming(open);
        }}
        title={`Fechar ${monthName(month)}${where}?`}
        description="O DRE do mês fica guardado como está agora e deixa de ser prévia."
        footer={
          <>
            <Button variant="ghost" disabled={close.isPending} onClick={() => setConfirming(false)}>
              Cancelar
            </Button>
            <Button disabled={close.isPending} onClick={() => close.mutate()}>
              {close.isPending ? 'Fechando…' : `Fechar ${monthName(month)}`}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p>
            Resultado de {monthName(month)}: <strong>{money(data.summary.resultCents)}</strong>.
          </p>
          {open ? (
            <p>
              Ficam em aberto: {openSentence(open.payables, 'despesa', 'despesas')} e{' '}
              {openSentence(open.receivables, 'receita', 'receitas')}
              {ack.pendingCommissionsCents > 0
                ? `, além de ${formatCurrency(ack.pendingCommissionsCents)} de comissão sem apurar`
                : ''}
              .
            </p>
          ) : null}
          <p className="text-content-muted">
            Depois de fechado, mudar algo de {monthName(month)} exige reabrir o mês ou lançar um
            ajuste de mês anterior.
          </p>
          {close.isError ? <RequestAlert message={getApiError(close.error)} /> : null}
        </div>
      </Modal>
    </>
  );
}

const openSentence = (side: { count: number; cents: number }, one: string, many: string) =>
  side.count === 0
    ? `nenhuma ${one}`
    : `${side.count} ${side.count === 1 ? one : many} (${formatCurrency(side.cents)})`;

/// O que continua em aberto depois de fechar (ADR 0064, decisão 18): não impede nada, porque o
/// pagamento conta no mês em que o dinheiro sai.
function OpenItems({
  open,
  can,
  onOpen,
}: {
  open: NonNullable<MonthClosingView['openItems']>;
  can: ClosingPermissions;
  onOpen: (destination: ClosingDestination) => void;
}) {
  const rows = [
    {
      label: 'Despesas sem pagar',
      side: open.payables,
      destination: 'payables' as const,
      action: 'Ver despesas',
    },
    {
      label: 'Receitas sem receber',
      side: open.receivables,
      destination: 'receivables' as const,
      action: 'Ver receitas',
    },
  ];
  return (
    <Section title="O que fica em aberto">
      <p className="mb-2 text-sm text-content-muted">
        Não impede fechar. O que tem competência até este mês continua em aberto e, quando for pago,
        conta no caixa do mês em que o dinheiro mexer.
      </p>
      <ul className="divide-y divide-surface-border rounded-xl border border-surface-border text-sm">
        {rows.map((row) => (
          <li
            key={row.label}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
          >
            <span>
              <span className="block font-medium">{row.label}</span>
              <span className="block text-content-muted">
                {row.side.count === 0
                  ? 'Nada em aberto.'
                  : `${row.side.count} ${row.side.count === 1 ? 'lançamento' : 'lançamentos'}: ${formatCurrency(row.side.cents)}`}
              </span>
            </span>
            {can.obligations && row.side.count > 0 ? (
              <Button size="sm" variant="outline" onClick={() => onOpen(row.destination)}>
                {row.action}
              </Button>
            ) : null}
          </li>
        ))}
        {open.commissions ? (
          <li className="px-4 py-3">
            <span className="block font-medium">Comissões</span>
            <span className="block text-content-muted">
              {formatCurrency(open.commissions.apuratedCents)} apurados neste mês, já no resultado;{' '}
              {open.commissions.pendingCents > 0
                ? `${formatCurrency(open.commissions.pendingCents)} sem apurar, fora do resultado.`
                : 'nada sem apurar.'}
            </span>
          </li>
        ) : null}
      </ul>
    </Section>
  );
}

function ClosingParts({ parts }: { parts: MonthClosingView['closingParts'] }) {
  const [open, setOpen] = useState(false);
  if (parts.length === 0) return null;
  return (
    <div className="mt-2">
      <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? 'Esconder as contas do plano' : 'Ver por conta do plano'}
      </Button>
      {open ? (
        <ul className="mt-2 space-y-1 rounded-xl border border-surface-border px-4 py-3 text-sm">
          {parts.map((part) => (
            <li key={part.account?.code ?? 'none'} className="flex justify-between gap-3">
              <span>{partLabel(part)}</span>
              <strong className="tabular-nums">{money(part.cents)}</strong>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function MonthSummary({ data }: { data: MonthClosingView }) {
  return (
    <SummaryStrip
      label="Resultado do mês"
      items={[
        { label: 'Receita líquida', value: money(data.summary.netRevenueCents) },
        { label: 'Custos e despesas', value: money(data.summary.costsCents) },
        {
          label: 'Resultado do mês',
          value: money(data.summary.resultCents),
          ...(data.summary.resultCents >= 0 ? { tone: 'positive' as const } : {}),
        },
      ]}
    />
  );
}

/// "Este valor pode estar contado duas vezes?" em linguagem comum (ADR 0064, decisões 10 e 11).
function DuplicateCard({
  question,
  canAnswer,
}: {
  question: DuplicateQuestion;
  canAnswer: boolean;
}) {
  const snackbar = useSnackbar();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const day = formatCivilDate(question.day).slice(0, 5);
  const declared = question.declared;
  const sameValue =
    declared.length > 1 && declared.every((item) => item.cents === declared[0]!.cents);
  const totalsDiffer = question.declaredCents !== question.salesCents;
  const smaller = Math.min(question.declaredCents, question.salesCents);
  const left = question.declaredCents - smaller;
  const undo = useMutation({
    mutationFn: () => closingApi.undoReview(question.day),
    onSuccess: async () => {
      snackbar.success(
        'Resposta desfeita. O valor volta a esperar uma resposta, e o mês não fecha enquanto isso.',
      );
      await refresh();
    },
    onError: (error) => snackbar.failure(getApiError(error)),
  });
  const answer = useMutation({
    mutationFn: (decision: 'SAME' | 'DIFFERENT') =>
      closingApi.review({
        day: question.day,
        decision,
        declaredCents: question.declaredCents,
        salesCents: question.salesCents,
      }),
    onSuccess: async (_, decision) => {
      snackbar.success(
        decision === 'SAME'
          ? `${formatCurrency(smaller)} de ${day} passa a contar uma vez.`
          : `Os valores de ${day} continuam contando os dois.`,
        { label: 'Desfazer', onAction: () => undo.mutate() },
      );
      await refresh();
    },
  });
  return (
    <div className="space-y-3 rounded-xl border border-dashed border-surface-border p-3">
      <p className="text-sm">
        {sameValue ? (
          <>
            <strong>
              {declared.length} entradas de {formatCurrency(declared[0]!.cents)} em {day}
            </strong>{' '}
            podem estar contadas duas vezes. Elas aparecem juntas porque a venda soma os pagamentos
            do dia por forma de pagamento.
          </>
        ) : (
          <>
            <strong>
              {formatCurrency(question.declaredCents)} em {day}
            </strong>{' '}
            aparecem duas vezes no resultado:
          </>
        )}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-lg bg-surface-soft px-3 py-2 text-sm">
          <span className="block text-xs text-content-muted">Lançado no extrato como receita</span>
          <span className="block">
            {declared.length === 1
              ? `${declared[0]!.description} · ${declared[0]!.detail}`
              : `${declared.length} entradas do extrato`}
          </span>
          <strong className="tabular-nums">{formatCurrency(question.declaredCents)}</strong>
        </div>
        <div className="rounded-lg bg-surface-soft px-3 py-2 text-sm">
          <span className="block text-xs text-content-muted">
            Receita das vendas ainda não conciliada
          </span>
          <span className="block">
            {question.sales.length === 1
              ? question.sales[0]!.description
              : `${question.sales.length} receitas do dia`}
          </span>
          <strong className="tabular-nums">{formatCurrency(question.salesCents)}</strong>
        </div>
      </div>
      {declared.length + question.sales.length > 2 ? (
        <div>
          <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? 'Esconder os lançamentos' : 'Ver os lançamentos'}
          </Button>
          {open ? (
            <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
              <ul className="max-h-56 space-y-0.5 overflow-auto rounded-lg bg-surface-soft px-3 py-2">
                {declared.map((item, index) => (
                  <li key={index}>
                    {item.description} · {formatCurrency(item.cents)}
                  </li>
                ))}
              </ul>
              <ul className="max-h-56 space-y-0.5 overflow-auto rounded-lg bg-surface-soft px-3 py-2">
                {question.sales.map((item, index) => (
                  <li key={index}>
                    {item.description} · {formatCurrency(item.cents)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
      {question.changedSinceReview ? (
        <p className="rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning">
          Os valores deste dia mudaram depois da resposta anterior. Responda de novo.
        </p>
      ) : null}
      {!question.review && totalsDiffer ? (
        <p className="rounded-lg bg-surface-soft px-3 py-2 text-sm text-content-muted">
          Os totais não batem. Se for o mesmo dinheiro, {formatCurrency(smaller)} passam a contar
          uma vez
          {left > 0
            ? ` e os ${formatCurrency(left)} a mais no extrato continuam como receita lançada no extrato (talvez falte registrar alguma venda)`
            : ''}
          .
        </p>
      ) : null}
      {question.review ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm">
            {question.review.decision === 'SAME'
              ? `Revisado: é o mesmo dinheiro. ${formatCurrency(smaller)} contam uma vez só, pela venda${left > 0 ? `; os ${formatCurrency(left)} a mais continuam como receita do extrato` : ''}.`
              : 'Revisado: são recebimentos diferentes. Os dois continuam contando.'}
            <span className="block text-xs text-content-muted">
              Respondido
              {question.review.reviewedByName
                ? ` por ${question.review.reviewedByName}`
                : ''} em {dateTime(question.review.reviewedAt)}.
            </span>
          </p>
          {canAnswer ? (
            <Button
              size="sm"
              variant="outline"
              disabled={undo.isPending}
              onClick={() => undo.mutate()}
            >
              Desfazer a resposta
            </Button>
          ) : null}
        </div>
      ) : canAnswer ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={answer.isPending}
            onClick={() => answer.mutate('SAME')}
          >
            É o mesmo dinheiro: contar uma vez
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={answer.isPending}
            onClick={() => answer.mutate('DIFFERENT')}
          >
            São recebimentos diferentes: contar os dois
          </Button>
        </div>
      ) : null}
      {answer.isError ? <RequestAlert message={getApiError(answer.error)} /> : null}
    </div>
  );
}

function partLabel(part: AccountPart): string {
  return part.account ? `${part.account.code} ${part.account.name}` : 'Sem conta do plano';
}

function AdjustmentList({
  adjustments,
  showOrigin,
}: {
  adjustments: PeriodAdjustment[];
  showOrigin?: boolean;
}) {
  return (
    <ul className="divide-y divide-surface-border rounded-xl border border-surface-border text-sm">
      {adjustments.map((one) => (
        <li key={one.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
          <span className="min-w-0">
            <span className="block font-medium">
              {showOrigin
                ? `Ajuste de ${monthName(one.originMonth)} · `
                : `Entra em ${monthName(one.targetMonth)} · `}
              {partLabel(one)}
            </span>
            <span className="block text-content-muted">Motivo: {one.reason}</span>
            <span className="block text-xs text-content-faint">
              Lançado{one.createdByName ? ` por ${one.createdByName}` : ''} em{' '}
              {dateTime(one.createdAt)}
            </span>
          </span>
          <strong className="tabular-nums">{money(one.cents)}</strong>
        </li>
      ))}
    </ul>
  );
}

function ClosedMonth({
  data,
  month,
  today,
  where,
  can,
}: {
  data: MonthClosingView;
  month: string;
  today: string;
  where: string;
  can: ClosingPermissions;
}) {
  const snackbar = useSnackbar();
  const refresh = useRefresh();
  const [reopening, setReopening] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [reason, setReason] = useState('');
  const current = data.history[0];
  const reopen = useMutation({
    mutationFn: () => closingApi.reopen({ month, today, reason }),
    onSuccess: async () => {
      setReopening(false);
      setReason('');
      snackbar.success(`${capital(monthName(month))} reaberto${where}.`);
      await refresh();
    },
  });
  const adjust = useMutation({
    mutationFn: () => closingApi.adjust({ month, reason }),
    onSuccess: async () => {
      setAdjusting(false);
      setReason('');
      snackbar.success(`Ajuste de ${monthName(month)} lançado em ${monthName(today.slice(0, 7))}.`);
      await refresh();
    },
  });
  const reasonError = (mutation: { isError: boolean; error: unknown }) =>
    mutation.isError ? getApiError(mutation.error) : undefined;
  return (
    <>
      <p className="mb-4 text-sm text-content-muted">
        Fechado{current?.closedByName ? ` por ${current.closedByName}` : ''}
        {current ? ` em ${dateTime(current.closedAt)}` : ''}. Os números abaixo são os que foram
        guardados e não mudam sozinhos. Para desfazer uma resposta dada no fechamento, reabra o mês.
      </p>
      <MonthSummary data={data} />
      <ClosingParts parts={data.closingParts} />
      {data.acknowledgement.pendingCommissionsCents > 0 ? (
        <p className="mt-4 text-sm text-content-muted">
          Ao fechar, {formatCurrency(data.acknowledgement.pendingCommissionsCents)} de comissão
          estavam sem apurar, e quem fechou confirmou que sabia.
        </p>
      ) : null}
      {data.change.length > 0 ? (
        <div className="mt-4 rounded-xl border border-warning/40 bg-warning/10 p-4">
          <p className="flex items-center gap-2 font-medium text-warning">
            <TriangleAlert className="size-4" aria-hidden="true" />
            {capital(monthName(month))} mudou depois de fechado
          </p>
          <p className="mt-1 text-sm">
            Algo deste mês foi gravado depois do fechamento. O DRE continua mostrando o número
            fechado, e a diferença espera uma decisão:
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {data.change.map((part, index) => (
              <li key={index} className="flex justify-between gap-3">
                <span>{partLabel(part)}</span>
                <strong className="tabular-nums">{money(part.cents)}</strong>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-content-muted">
            Lance como ajuste se os números de {monthName(month)} já foram enviados aos sócios ou ao
            contador. Reabra se ainda não saíram da empresa.
          </p>
        </div>
      ) : null}
      {data.adjustmentsFrom.length > 0 ? (
        <Section title={`Ajustes lançados por causa de ${monthName(month)}`}>
          <AdjustmentList adjustments={data.adjustmentsFrom} />
        </Section>
      ) : null}
      <Section title="Histórico">
        <ul className="space-y-1 text-sm text-content-muted">
          {data.history.map((version) => (
            <li key={version.version} className="flex items-start gap-2">
              <History className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>
                Versão {version.version}: fechada
                {version.closedByName ? ` por ${version.closedByName}` : ''} em{' '}
                {dateTime(version.closedAt)}
                {version.status === 'REOPENED'
                  ? `; reaberta${version.reopenedByName ? ` por ${version.reopenedByName}` : ''}${version.reopenedAt ? ` em ${dateTime(version.reopenedAt)}` : ''}${version.reopenedReason ? `. Motivo: ${version.reopenedReason}` : ''}`
                  : ''}
                .
              </span>
            </li>
          ))}
        </ul>
      </Section>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {can.adjust && data.change.length > 0 ? (
          <Button variant="outline" onClick={() => setAdjusting(true)}>
            Lançar a diferença como ajuste
          </Button>
        ) : null}
        {can.reopen ? (
          <Button variant="outline" onClick={() => setReopening(true)}>
            Reabrir {monthName(month)}
          </Button>
        ) : null}
      </div>
      <Modal
        open={reopening}
        onOpenChange={(open) => {
          if (!reopen.isPending) setReopening(open);
        }}
        title={`Reabrir ${monthName(month)}${where}?`}
        description="Use quando os números ainda não foram enviados a ninguém. Se já foram para os sócios ou para o contador, prefira lançar um ajuste."
        footer={
          <>
            <Button variant="ghost" disabled={reopen.isPending} onClick={() => setReopening(false)}>
              Cancelar
            </Button>
            <Button disabled={reopen.isPending} onClick={() => reopen.mutate()}>
              {reopen.isPending ? 'Reabrindo…' : `Reabrir ${monthName(month)}`}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p>
            {monthName(month)} e os meses seguintes que estiverem fechados voltam a ser prévia,
            porque o saldo de cada mês depende do anterior. Ajustes lançados por causa desses meses
            são cancelados: a correção passa a contar no próprio mês. As versões fechadas continuam
            no histórico.
          </p>
          <Textarea
            label="Motivo"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ex.: a conta de luz de agosto foi lançada em Aluguel."
            error={reasonError(reopen)}
          />
        </div>
      </Modal>
      <Modal
        open={adjusting}
        onOpenChange={(open) => {
          if (!adjust.isPending) setAdjusting(open);
        }}
        title={`Lançar ajuste de ${monthName(month)}?`}
        description={`${capital(monthName(month))} continua como foi fechado. A diferença entra em ${monthName(today.slice(0, 7))}, na linha "Ajuste de ${monthName(month)}", abaixo do resultado e fora dele.`}
        footer={
          <>
            <Button variant="ghost" disabled={adjust.isPending} onClick={() => setAdjusting(false)}>
              Cancelar
            </Button>
            <Button disabled={adjust.isPending} onClick={() => adjust.mutate()}>
              {adjust.isPending ? 'Lançando…' : 'Lançar ajuste'}
            </Button>
          </>
        }
      >
        <Textarea
          label="Motivo do ajuste"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Ex.: devolução registrada depois do fechamento."
          error={reasonError(adjust)}
        />
      </Modal>
    </>
  );
}
